// src/guest.js
// Using the app without an account: the tournaments live on this device only (the offline engine, under an id of the
// guest's own), until the guest signs in and chooses to move them into their account.
const ID_KEY = 'tournamental.guestId'; // stays until the tournaments have moved to an account
const ACTIVE_KEY = 'tournamental.guestActive'; // whether the app is currently being used as the guest
const DECLINED_KEY = 'tournamental.guestDeclined'; // the guest's tournaments were offered to an account and the person said no
const listeners = new Set();

export const isGuestId = (uid) => typeof uid === 'string' && uid.startsWith('guest-');

const read = (key) => {
    try {
        return window.localStorage.getItem(key);
    } catch (error) {
        return null;
    }
};

const write = (key, value) => {
    try {
        if (value === null) window.localStorage.removeItem(key);
        else window.localStorage.setItem(key, value);
    } catch (error) {
        // no storage: a guest cannot be remembered
    }
};

export const guestId = () => read(ID_KEY);
export const guestActive = () => read(ACTIVE_KEY) === '1' && Boolean(guestId());
// After the person said no to moving the tournaments into an account, they are not asked again (until they are a guest once more).
export const guestDeclined = () => read(DECLINED_KEY) === '1';
export const setGuestDeclined = (declined) => write(DECLINED_KEY, declined ? '1' : null);

export const guestUser = () => ({ uid: guestId(), displayName: 'Guest', isGuest: true });

const notify = () => listeners.forEach((listener) => listener());

export function subscribeToGuest(listener) {
    listeners.add(listener);
    return () => listeners.delete(listener);
}

// Starts (or resumes) being the guest of this device.
export function startGuest() {
    if (!guestId()) {
        const bytes = new Uint8Array(8);
        if (window.crypto && window.crypto.getRandomValues) window.crypto.getRandomValues(bytes);
        else bytes.forEach((_, index) => (bytes[index] = Math.floor(Math.random() * 256))); // (old browsers, tests)
        write(ID_KEY, 'guest-' + Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join(''));
    }
    write(ACTIVE_KEY, '1');
    write(DECLINED_KEY, null); // (a new guest session: the question is open again)
    notify();
    return guestUser();
}

// Stops being the guest; what the guest made stays on the device (starting again finds it).
export function endGuestSession() {
    write(ACTIVE_KEY, null);
    notify();
}

// Forgets the guest for good (their tournaments have moved to an account).
export function forgetGuest() {
    write(ACTIVE_KEY, null);
    write(ID_KEY, null);
    write(DECLINED_KEY, null);
    notify();
}
